import React, { useMemo } from 'react';
import { CubeIcon } from '@heroicons/react/24/outline';

import { Button, Combobox, Label } from '../../../components/ui';
import { BVIndexedProduct } from '../types';

interface ProductSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  products: BVIndexedProduct[];
  disabled?: boolean;
  isLoading?: boolean;
  hasBrandSelected?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
}

export function ProductSelector({
  value,
  onChange,
  products,
  disabled = false,
  isLoading = false,
  hasBrandSelected = false,
  loadError = null,
  onRetry,
}: ProductSelectorProps) {
  const options = useMemo(
    () =>
      products.map(product => ({
        value: product.product_name,
        label: product.product_name,
        description: `${product.count.toLocaleString()} indexed reviews`,
        icon: <CubeIcon className="h-4 w-4 text-gray-400" />,
      })),
    [products],
  );

  const placeholder = !hasBrandSelected
    ? 'Select a brand first'
    : isLoading
      ? 'Loading products...'
      : products.length > 0
        ? 'Select an indexed product'
        : 'No indexed products found for this brand';

  const helperText = !hasBrandSelected
    ? 'Choose a brand to unlock product search.'
    : isLoading
      ? 'Loading indexed products for the selected brand.'
      : loadError
        ? loadError
        : products.length > 0
          ? `${products.length.toLocaleString()} indexed product${products.length === 1 ? '' : 's'} available for the selected brand.`
          : 'No indexed products are currently available for the selected brand.';

  return (
    <div className="space-y-2">
      <Label>Product (optional)</Label>
      <Combobox
        options={options}
        value={value}
        onChange={nextValue => onChange(nextValue)}
        placeholder={placeholder}
        searchPlaceholder="Search products..."
        emptyMessage="No matching product found."
        clearable
        disabled={disabled || !hasBrandSelected || isLoading}
      />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
        {hasBrandSelected && !isLoading && onRetry && (Boolean(loadError) || products.length === 0) ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0 py-0 text-xs text-gray-500 hover:bg-transparent hover:text-charcoal dark:text-gray-400 dark:hover:bg-transparent dark:hover:text-gray-100"
            onClick={onRetry}
          >
            Refresh products
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default ProductSelector;
